/**
 * Guess-the-word Facebook helpers for Word Wheel Social.
 */

export function parseWordsInUse(wordsInUse) {
    return String(wordsInUse || "")
        .split(/\r?\n/)
        .map((w) => w.trim().toUpperCase())
        .filter((w) => /^[A-Z]+$/.test(w));
}

/** Same blocked categories as Word Wheel templates (`high_frequency` is allowed). */
export const WORD_WHEEL_BLOCKED_BANNED_CATEGORIES = new Set([
    "profanity",
    "violence",
    "drugs",
    "offensive",
    "inappropriate",
    "other",
]);

export function blockedBannedKeys(bannedList) {
    const keys = new Set();
    if (!Array.isArray(bannedList)) return keys;
    for (const banned of bannedList) {
        if (!banned) continue;
        const category = String(banned.category || "").trim().toLowerCase();
        if (!WORD_WHEEL_BLOCKED_BANNED_CATEGORIES.has(category)) continue;
        if (banned.word) keys.add(String(banned.word).trim().toLowerCase());
        if (banned.wordNorm) keys.add(String(banned.wordNorm).trim().toLowerCase());
    }
    return keys;
}

/** Returns `{ word, category }` when the word is on the blocked banned list. */
export function matchBannedWord(bannedList, word) {
    const target = String(word || "").trim().toLowerCase();
    if (!target || !Array.isArray(bannedList)) return null;
    for (const banned of bannedList) {
        if (!banned) continue;
        const category = String(banned.category || "").trim().toLowerCase();
        if (!WORD_WHEEL_BLOCKED_BANNED_CATEGORIES.has(category)) continue;
        const w = String(banned.word || "").trim().toLowerCase();
        const n = String(banned.wordNorm || "").trim().toLowerCase();
        if (w === target || n === target) {
            return {
                word: banned.word || word,
                category: banned.category || category,
            };
        }
    }
    return null;
}

/** Dictionary {@code info.score}. Higher = more common / easier. */
export function scoreByWordFromDictionary(rows) {
    const map = new Map();
    if (!Array.isArray(rows)) return map;
    for (const row of rows) {
        const word = String(row?.word || "").trim().toUpperCase();
        const n = Number(row?.info?.score);
        if (word && Number.isFinite(n)) {
            map.set(word, n);
        }
    }
    return map;
}

export function mergeWordScores(preferred, fallback) {
    const map = new Map(fallback instanceof Map ? fallback : []);
    if (preferred instanceof Map) {
        for (const [key, value] of preferred) {
            if (Number.isFinite(value) && value > 0) {
                map.set(key, value);
            }
        }
    }
    return map;
}

/** Puzzle details scores: prefer {@code info.score}, else Datamuse {@code item.score}. */
export function scoreByWordFromDetails(details) {
    const map = new Map();
    let parsed = details;
    if (typeof details === "string") {
        try {
            parsed = JSON.parse(details);
        } catch {
            return map;
        }
    }
    if (!parsed || typeof parsed !== "object") return map;
    const nested = parsed.wordScores && typeof parsed.wordScores === "object" ? parsed.wordScores : null;
    const rows = Array.isArray(parsed.scores)
        ? parsed.scores
        : Array.isArray(nested?.scores)
            ? nested.scores
            : [];
    for (const row of rows) {
        const word = String(row?.word || "").trim().toUpperCase();
        const raw = row?.info?.score ?? row?.item?.score ?? row?.score;
        const n = Number(raw);
        if (word && Number.isFinite(n)) {
            map.set(word, n);
        }
    }
    return map;
}

function focusWordPool(words, excludeKeys = null) {
    const list = Array.isArray(words) ? words : [];
    const exclude = excludeKeys instanceof Set ? excludeKeys : null;
    const allowed = exclude
        ? list.filter((w) => !exclude.has(String(w).toLowerCase()))
        : list;
    const source = allowed.length > 0 ? allowed : list;
    const preferred = source.filter((w) => w.length >= 4 && w.length <= 7);
    const pool = preferred.length > 0 ? preferred : source.filter((w) => w.length >= 3);
    return pool.length > 0 ? pool : source;
}

function compareWordEase(a, b, scoreByWord) {
    if (a.length !== b.length) {
        return a.length - b.length;
    }
    const sa = scoreByWord instanceof Map ? (scoreByWord.get(String(a).toUpperCase()) || 0) : 0;
    const sb = scoreByWord instanceof Map ? (scoreByWord.get(String(b).toUpperCase()) || 0) : 0;
    if (sa !== sb) {
        return sb - sa;
    }
    return String(a).localeCompare(String(b));
}

function rankFocusWords(words, excludeKeys = null, scoreByWord = null) {
    const use = focusWordPool(words, excludeKeys);
    return [...use].sort((a, b) => compareWordEase(a, b, scoreByWord));
}

/**
 * Easiest share word: shortest in the 4–7 letter band (else 3+), then highest dictionary info.score.
 * `rng` is unused; kept so existing call sites stay valid.
 */
export function pickFocusWord(words, rng = Math.random, excludeKeys = null, scoreByWord = null) {
    const ranked = rankFocusWords(words, excludeKeys, scoreByWord);
    return ranked[0] || "";
}

/** Cycle easiest → next easiest (for Shuffle word). */
export function pickNextFocusWord(words, currentWord, excludeKeys = null, scoreByWord = null) {
    const ranked = rankFocusWords(words, excludeKeys, scoreByWord);
    if (ranked.length === 0) return "";
    const cur = String(currentWord || "").trim().toUpperCase();
    const i = ranked.findIndex((w) => String(w).toUpperCase() === cur);
    if (i < 0) return ranked[0];
    return ranked[(i + 1) % ranked.length];
}

const VOWELS = new Set(["A", "E", "I", "O", "U"]);

export function normalizeGuessWord(word) {
    return String(word || "")
        .toUpperCase()
        .replace(/[^A-Z]/g, "");
}

function revealKey(reveal) {
    return reveal.map((shown) => (shown ? "1" : "0")).join("");
}

function isValidReveal(reveal) {
    const n = reveal.length;
    const shown = reveal.filter(Boolean).length;
    const blank = n - shown;
    if (shown < 1 || blank < 1) return false;
    return true;
}

function addReveal(out, seen, reveal) {
    if (!isValidReveal(reveal)) return;
    const key = revealKey(reveal);
    if (seen.has(key)) return;
    seen.add(key);
    out.push(reveal);
}

function revealAt(n, indices) {
    const reveal = Array(n).fill(false);
    for (const i of indices) {
        if (i >= 0 && i < n) reveal[i] = true;
    }
    return reveal;
}

/** Pattern family for GOOD: G___, G_OD, _OO_, GO__, … — not only G__D. */
export function maskRevealCandidates(word) {
    const w = normalizeGuessWord(word);
    const n = w.length;
    if (n === 0) return [];
    if (n <= 2) return [Array(n).fill(true)];
    const out = [];
    const seen = new Set();
    const add = (reveal) => addReveal(out, seen, reveal);
    add(revealAt(n, [0]));
    add(revealAt(n, [n - 1]));
    add(revealAt(n, [0, n - 1]));
    add(Array.from({ length: n }, (_, i) => i < 2));
    add(Array.from({ length: n }, (_, i) => i >= n - 2));
    if (n >= 4) {
        add(revealAt(n, [0, n - 2, n - 1]));
        add(revealAt(n, [0, 1, n - 1]));
    }
    if (n >= 5) {
        add(Array.from({ length: n }, (_, i) => i < 3));
        add(Array.from({ length: n }, (_, i) => i >= n - 3));
        add(revealAt(n, [0, Math.floor(n / 2), n - 1]));
    }
    add(w.split("").map((ch) => VOWELS.has(ch)));
    add(w.split("").map((ch) => !VOWELS.has(ch)));
    add(Array.from({ length: n }, (_, i) => i % 2 === 0));
    add(Array.from({ length: n }, (_, i) => i % 2 === 1));
    return out.length > 0 ? out : [revealAt(n, [0, n - 1])];
}

function slotsFromReveal(word, reveal) {
    return word.split("").map((ch, i) => (reveal[i] ? ch : ""));
}

/** Random blanks for a new Facebook / Instagram card. */
export function pickMaskWordSlots(word, rng = Math.random) {
    const w = normalizeGuessWord(word);
    if (!w) return [];
    const options = maskRevealCandidates(w);
    const reveal = options[Math.floor(rng() * options.length) % options.length];
    return slotsFromReveal(w, reveal);
}

export function slotsFromMaskedCaption(masked, word) {
    const w = normalizeGuessWord(word);
    const compact = String(masked || "")
        .toUpperCase()
        .replace(/\s+/g, "");
    if (!w) return [];
    if (compact.length !== w.length) return pickMaskWordSlots(w);
    return w.split("").map((_, i) => {
        const m = compact[i];
        return m === "_" || m === "-" || m === "·" || m === "•" ? "" : w[i];
    });
}

export function maskWordSlots(word, maskedCaption) {
    if (maskedCaption) return slotsFromMaskedCaption(maskedCaption, word);
    return pickMaskWordSlots(word);
}

export function formatSlotsCaption(slots) {
    return (slots || []).map((ch) => (ch ? ch : "_")).join(" ");
}

/** Caption line: G _ O D */
export function formatMaskedCaption(word, slots) {
    if (Array.isArray(slots) && slots.length > 0) {
        return formatSlotsCaption(slots);
    }
    return formatSlotsCaption(maskWordSlots(word));
}

/** Short display-clue definition for the admin answer line. */
export function lookupShortMeaning(displayClue, word, maxLen = 90) {
    const target = String(word || "").trim().toUpperCase();
    if (!target || !Array.isArray(displayClue)) return "";
    const row = displayClue.find(
        (item) => String(item?.word || "").trim().toUpperCase() === target,
    );
    const def = String(row?.definition || "").trim();
    if (!def) return "";
    if (def.length <= maxLen) return def;
    const cut = def.slice(0, maxLen).replace(/\s+\S*$/, "").trim();
    return `${cut || def.slice(0, maxLen)}…`;
}

/** Distinct two-stop backgrounds for the guess-the-word card (no photo). */
export const WARM_GRADIENT_SETS = [
    { id: "orange", name: "Orange", from: "#c2410c", to: "#fb923c", angle: 140 },
    { id: "blue", name: "Blue", from: "#1e3a8a", to: "#38bdf8", angle: 145 },
    { id: "green", name: "Green", from: "#14532d", to: "#4ade80", angle: 135 },
    { id: "teal", name: "Teal", from: "#115e59", to: "#2dd4bf", angle: 138 },
    { id: "purple", name: "Purple", from: "#4c1d95", to: "#c084fc", angle: 148 },
    { id: "red", name: "Red", from: "#991b1b", to: "#f87171", angle: 132 },
    { id: "gold", name: "Gold", from: "#a16207", to: "#facc15", angle: 128 },
    { id: "navy", name: "Navy", from: "#0f172a", to: "#3b82f6", angle: 150 },
    { id: "magenta", name: "Magenta", from: "#9d174d", to: "#f472b6", angle: 142 },
    { id: "lime", name: "Lime", from: "#3f6212", to: "#a3e635", angle: 136 },
];

export function gradientCss(set) {
    const use = set || WARM_GRADIENT_SETS[0];
    return `linear-gradient(${use.angle}deg, ${use.from} 0%, ${use.to} 100%)`;
}

export function pickWarmGradientSet(rng = Math.random) {
    const i = Math.floor(rng() * WARM_GRADIENT_SETS.length) % WARM_GRADIENT_SETS.length;
    return WARM_GRADIENT_SETS[i];
}

/** Next named background so consecutive publishes are not the same color. */
export function nextWarmGradientSet(lastId) {
    const list = WARM_GRADIENT_SETS;
    if (!list.length) return null;
    const key = String(lastId || "").trim().toLowerCase();
    const idx = list.findIndex((s) => s.id === key);
    if (idx < 0) return list[0];
    return list[(idx + 1) % list.length];
}

export function pickWarmGradient(rng = Math.random) {
    return gradientCss(pickWarmGradientSet(rng));
}

export const GUESS_QUESTIONS = [
    "Got a second? What's this word?",
    "This one's on the tip of my tongue…",
    "Help me out — what word is this?",
    "Quick one for you. What's the word?",
    "Don't scroll past this one.",
    "I bet you get this in three seconds.",
    "Too easy? Prove it in the comments.",
    "Your brain knows this. Type it.",
    "Tiny puzzle for your coffee break.",
    "One word. No pressure.",
    "Morning warm-up: what's the word?",
    "Save this for later… or guess now.",
    "Drop your guess below.",
    "First to comment gets bragging rights.",
    "Wrong guesses welcome.",
    "I'll wait. What's the word?",
];

export function pickGuessQuestion(rng = Math.random) {
    const list = GUESS_QUESTIONS;
    if (!list.length) return "Got a second? What's this word?";
    return list[Math.floor(rng() * list.length) % list.length];
}

export function formatWheelCaption(letters) {
    const list = (letters || []).map((ch) => String(ch || "").toUpperCase()).filter(Boolean);
    if (list.length === 0) return "";
    return `wheel has ${list.join(" + ")}`;
}

export function formatGridCaption() {
    return "Find it in the grid.";
}

export function numberedClue(clue, number) {
    const text = String(clue || "").trim();
    if (!text) return "";
    const n = Number(number);
    if (!Number.isFinite(n) || n < 1) return text;
    if (/^\d+\.\s/.test(text)) return text;
    return `${n}. ${text}`;
}

export function buildGuessCaption({ headline, word, letters, hashtags, playStoreUrl, slots, clue, showGrid = false }) {
    const lines = [];
    if (headline) lines.push(headline);
    const masked = formatMaskedCaption(word, slots);
    if (masked) lines.push(masked);
    // Clue hidden on social posts for now.
    // if (clue) lines.push(clue);
    if (!showGrid) {
        const wheelLine = formatWheelCaption(letters);
        if (wheelLine) lines.push(wheelLine);
    }
    if (hashtags) {
        lines.push("");
        lines.push(hashtags);
    }
    if (playStoreUrl) {
        lines.push("");
        lines.push("Play Word Wheel Quest free on Android:");
        lines.push(playStoreUrl);
    }
    return lines.join("\n");
}

export function isPublishedToday(publishedAt) {
    if (!publishedAt) return false;
    const d = new Date(publishedAt);
    if (Number.isNaN(d.getTime())) return false;
    const now = new Date();
    return (
        d.getFullYear() === now.getFullYear()
        && d.getMonth() === now.getMonth()
        && d.getDate() === now.getDate()
    );
}
